import React from 'react';
import PropTypes from 'prop-types';

import { Stack, Grid, Box, Button, Typography, Select, MultiSelect } from '@metarouter-private/mr-mui';

import useFeatureEnabled from 'app/launch-darkly/use-feature-enabled';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import WithTooltip from 'app/components/WithTooltip';
import { useBulkPlaybookUpdateTableContext } from 'ion-experimental/app/bulk-playbook-update/BulkPlaybookUpdateTableContext';
import {
  useBulkPlaybookUpdateModalContext,
  BULK_PLAYBOOK_UPDATE_MODALS,
} from 'ion-experimental/app/bulk-playbook-update/BulkPlaybookUpdateModalContext';
import BulkPlaybookUpdateClusterMultiSelect from './BulkPlaybookUpdateClusterMultiSelect';
import BulkPlaybookUpdateSearchBar from './BulkPlaybookUpdateSearchBar';

export default function BulkPlaybookUpdateTableTitle({ onCancel }) {
  const {
    // Filter state
    filterIntegrationId,
    setFilterIntegrationId,
    filterPlaybookId,
    setFilterPlaybookId,
    // Target state
    selectedIntegrationId,
    setSelectedIntegrationId,
    selectedPlaybookId,
    setSelectedPlaybookId,
    hasTargetPlaybook,
    // Selection state
    selectedCount,
    // Integration data (filters include "All" options, target options don't)
    integrationOptions,
    targetIntegrationOptions,
    revisionsByIntegrationId,
    targetRevisionsByIntegrationId,
    integrationsLoading,
    // Tags filter
    selectedTagIds,
    setSelectedTagIds,
    tagOptions,
    tagsLoading,
    // Constants
    DEFAULT_INTEGRATION_OPTION,
    DEFAULT_REVISION_OPTION,
  } = useBulkPlaybookUpdateTableContext();

  const { onOpenModal } = useBulkPlaybookUpdateModalContext();

  const { pipelineTagsMvpEnabled } = useFeatureEnabled(FEATURE_FLAGS.pipelineTagsMvp);

  const hasSelection = selectedCount > 0;

  const handleFilterIntegrationChange = event => {
    setFilterIntegrationId(event.target.value);
  };

  const handleFilterRevisionChange = event => {
    setFilterPlaybookId(event.target.value);
  };

  const handleTargetIntegrationChange = event => {
    setSelectedIntegrationId(event.target.value);
  };

  const handleTargetRevisionChange = event => {
    setSelectedPlaybookId(event.target.value);
  };

  const handleConfirm = () => {
    onOpenModal(BULK_PLAYBOOK_UPDATE_MODALS.CONFIRM_UPDATE);
  };

  const handleTagsChange = selectedValues => {
    setSelectedTagIds(selectedValues);
  };

  const handleCancel = () => {
    if (hasSelection) {
      onOpenModal(BULK_PLAYBOOK_UPDATE_MODALS.CONFIRM_CANCEL);
    } else {
      onCancel();
    }
  };

  const filterRevisionOptions = revisionsByIntegrationId[filterIntegrationId] || [DEFAULT_REVISION_OPTION];
  const targetRevisionOptions = targetRevisionsByIntegrationId[selectedIntegrationId] || [];

  const isFilterRevisionDisabled = filterIntegrationId === DEFAULT_INTEGRATION_OPTION.value;
  const isTargetRevisionDisabled = !selectedIntegrationId || targetRevisionOptions.length === 0;
  const isConfirmDisabled = !hasTargetPlaybook || !hasSelection;

  return (
    <Stack direction="column" spacing={2}>
      <Box pl={1}>
        <Typography variant="h6">Apply Integration to Selected Pipelines</Typography>
      </Box>

      <Grid container spacing={2} alignItems="center" borderBottom={1} pb={2} borderColor="divider">
        <Grid item alignItems="center" flexGrow={1}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={8}>
              <Select
                label="Integration"
                size="small"
                variant="outlined"
                value={selectedIntegrationId}
                options={targetIntegrationOptions}
                onChange={handleTargetIntegrationChange}
                disabled={integrationsLoading}
                data-test-id="targetIntegrationSelect"
              />
            </Grid>
            <Grid item xs={4}>
              <Select
                label="Integration Version"
                size="small"
                variant="outlined"
                value={selectedPlaybookId}
                options={targetRevisionOptions}
                onChange={handleTargetRevisionChange}
                disabled={isTargetRevisionDisabled || integrationsLoading}
                data-test-id="targetRevisionSelect"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Row 1: Cluster + Search */}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={3}>
          <BulkPlaybookUpdateClusterMultiSelect />
        </Grid>
        <Grid item flexGrow={1}>
          <BulkPlaybookUpdateSearchBar />
        </Grid>
      </Grid>

      {/* Row 2: Integration, Revision, and Tags Filters */}
      <Grid container spacing={1} alignItems="center">
        <Grid item flexGrow={1}>
          <Stack direction="row" spacing={1}>
            <Select
              label="Integration"
              size="small"
              variant="outlined"
              value={filterIntegrationId}
              options={integrationOptions}
              onChange={handleFilterIntegrationChange}
              disabled={integrationsLoading}
              data-test-id="filterIntegrationSelect"
            />
            <Select
              label="Revision"
              size="small"
              variant="outlined"
              value={filterPlaybookId}
              options={filterRevisionOptions}
              onChange={handleFilterRevisionChange}
              disabled={isFilterRevisionDisabled || integrationsLoading}
              data-test-id="filterRevisionSelect"
            />
          </Stack>
        </Grid>
        {pipelineTagsMvpEnabled && (
          <Grid item xs={3} width={0}>
            <MultiSelect
              label="Tags"
              size="small"
              variant="outlined"
              value={selectedTagIds}
              options={tagOptions}
              onChange={handleTagsChange}
              disabled={tagsLoading}
              data-test-id="filterTagsMultiSelect"
            />
          </Grid>
        )}
      </Grid>

      {/* Row 3: Target Selection + Actions */}
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Typography variant="body2" color="text.secondary">
            {buildSelectedCountMessage(selectedCount)}
          </Typography>
        </Grid>
        <Grid item flexGrow={1}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleCancel} data-test-id="cancelButton">
              Cancel
            </Button>

            <WithTooltip
              show={isConfirmDisabled}
              title={buildConfirmDisabledMessage({ hasTargetPlaybook, hasSelection })}
            >
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                data-test-id="confirmButton"
              >
                Confirm Selection
              </Button>
            </WithTooltip>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}

BulkPlaybookUpdateTableTitle.propTypes = {
  onCancel: PropTypes.func.isRequired,
};

function buildSelectedCountMessage(count) {
  switch (count) {
    case 0:
      return 'No pipelines selected';
    case 1:
      return '1 pipeline selected';
    default:
      return `${count} pipelines selected`;
  }
}

function buildConfirmDisabledMessage({ hasTargetPlaybook, hasSelection }) {
  if (!hasTargetPlaybook) {
    return 'Select a target integration';
  }

  if (!hasSelection) {
    return 'Select pipelines to update';
  }

  return null;
}
